import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Shield, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { RadialBarChart, RadialBar, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function CyberScore() {
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(null);

  useEffect(() => {
    fetchScore();
  }, []);

  const fetchScore = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cyber-scores/my`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setScore(data);
      } else if (response.status === 404) {
        // No score yet
        setScore(null);
      }
    } catch (error) {
      toast.error('Failed to load cyber score');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (scoreValue) => {
    if (scoreValue >= 800) return 'text-primary';
    if (scoreValue >= 650) return 'text-accent';
    if (scoreValue >= 500) return 'text-secondary';
    return 'text-destructive';
  };

  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return 'bg-primary/20 text-primary border-primary/30';
    if (grade.startsWith('B')) return 'bg-accent/20 text-accent border-accent/30';
    if (grade.startsWith('C')) return 'bg-secondary/20 text-secondary border-secondary/30';
    return 'bg-destructive/20 text-destructive border-destructive/30';
  };

  if (loading) {
    return (
      <DashboardLayout title="Cyber Score">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!score) {
    return (
      <DashboardLayout title="Cyber Score">
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-mono font-bold mb-2">Cyber Score Not Available</h3>
          <p className="text-muted-foreground">Your cyber security score will be available soon</p>
        </div>
      </DashboardLayout>
    );
  }

  const chartData = [
    {
      name: 'Score',
      value: score.overall_score,
      fill: score.overall_score >= 800 ? '#00F8B3' : score.overall_score >= 650 ? '#5A8EF5' : '#FF6B6B'
    }
  ];

  const categories = [
    { name: 'Network Security', value: score.network_security, icon: Shield },
    { name: 'Endpoint Security', value: score.endpoint_security, icon: Shield },
    { name: 'Patching Cadence', value: score.patching_cadence, icon: TrendingUp },
    { name: 'Identity Management', value: score.identity_management, icon: Shield },
    { name: 'Email Security', value: score.email_security, icon: Shield }
  ];

  return (
    <DashboardLayout title="Cyber Score">
      <div className="space-y-6" data-testid="cyber-score-page">
        {/* Main Score Card */}
        <div className="bg-gradient-to-br from-card to-card/50 border border-border rounded-lg p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Score Display */}
            <div className="flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="h-8 w-8 text-primary" />
                <h2 className="text-2xl font-mono font-bold">Cyber Security Score</h2>
              </div>
              
              <div className="flex items-end gap-6 mb-6">
                <div>
                  <div className={`text-7xl font-mono font-bold ${getScoreColor(score.overall_score)}`}>
                    {score.overall_score}
                  </div>
                  <p className="text-sm text-muted-foreground font-mono mt-2">out of 1000</p>
                </div>
                
                <div className={`px-6 py-3 rounded-lg border-2 ${getGradeColor(score.grade)}`}>
                  <div className="text-4xl font-mono font-bold">{score.grade}</div>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-muted-foreground"></div>
                  <span className="text-muted-foreground font-mono">Industry Avg: {score.industry_average}</span>
                </div>
                {score.overall_score > score.industry_average ? (
                  <span className="text-primary font-mono font-bold">
                    +{score.overall_score - score.industry_average} above average
                  </span>
                ) : (
                  <span className="text-destructive font-mono font-bold">
                    {score.overall_score - score.industry_average} below average
                  </span>
                )}
              </div>

              <p className="text-xs text-muted-foreground font-mono mt-4">
                Last updated: {new Date(score.last_updated).toLocaleDateString()}
              </p>
            </div>

            {/* Radial Chart */}
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={300}>
                <RadialBarChart
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="100%"
                  barSize={20}
                  data={chartData}
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar
                    minAngle={15}
                    background
                    clockWise
                    dataKey="value"
                    cornerRadius={10}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-xl font-mono font-bold mb-6">Security Categories</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category, index) => {
              const Icon = category.icon;
              const percentage = (category.value / 10);
              return (
                <div key={index} className="bg-background border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary" />
                      <span className="text-sm font-mono font-bold">{category.name}</span>
                    </div>
                    <span className={`text-lg font-mono font-bold ${getScoreColor(category.value)}`}>
                      {category.value}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Strengths */}
          {score.strengths && score.strengths.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-mono font-bold">Strengths</h3>
              </div>
              <div className="space-y-2">
                {score.strengths.map((strength, index) => (
                  <div key={index} className="flex items-start gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                    <span className="font-mono">{strength}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Risk Factors */}
          {score.risk_factors && score.risk_factors.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <h3 className="text-lg font-mono font-bold">Risk Factors</h3>
              </div>
              <div className="space-y-2">
                {score.risk_factors.map((risk, index) => (
                  <div key={index} className="flex items-start gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-destructive mt-1.5 flex-shrink-0"></div>
                    <span className="font-mono">{risk}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default CyberScore;
